var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import cookie from 'js-cookie';
import axios from 'axios';
import { getVisibleSteps, createTicket } from './Signup.utils';
import isEmailAddressValid from '../../Utils/IsEmailAddressValid';
// Steps
import AdvisorStep from './AdvisorStep';
import UserAdvisorStep from './UserAdvisorStep';
import UserInfoStep from './UserInfoStep';
import MaritalStatusAndBookingStep from './MaritalStatusAndBookingStep';
import ConsentsStep from './ConsentsStep';
import AddDocumentsStep from './AddDocumentsStep';
import AdvancedStep from './AdvancedStep';
import EndStep from './EndStep';
import Message from '../../ComponentMessage';
import StepWrapper from './components/StepWrapper';
import ErrorBoundary from '../../Components/ComponentErrorBoundary';
import { UserToUpdate, Steps, UserFields } from './SignupForm.constants';
import T from './Locale';
const { USER_INFO, MARITAL_STATUS_AND_BOOKING, ADD_DOCUMENTS, ADVANCED, ADVISOR, END, CONSENT } = Steps;
const { ENABLED_DOCUMENTS_AND_TIME_BOOKING, ADVISOR_ID, BRANCH_ID, IS_ADVISOR, PARTNER_PRESENT, FIRST_NAME, LAST_NAME, EMAIL, PHONE } = UserFields;
const SignupForm = ({ partner, branches, isAdvisor = true, forceAdvisorField = false }) => {
    var _a, _b, _c, _d;
    const initialUserData = {
        [IS_ADVISOR]: isAdvisor,
        advisorId: (_a = cookie.get(partner.id + ADVISOR_ID)) !== null && _a !== void 0 ? _a : '',
        branchId: (_b = cookie.get(partner.id + BRANCH_ID)) !== null && _b !== void 0 ? _b : ''
    };
    const [firstUser, setFirstUser] = useState(initialUserData);
    const [additionalData, setAdditionalData] = useState({ products: [], isAdvisor: isAdvisor });
    const [secondUser, setSecondUser] = useState(initialUserData);
    const [currentStep, setCurrentStep] = useState(isAdvisor ? ADVISOR : USER_INFO);
    const [isCreatUserLoading, setCreateUserLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [bookingPackageInfo, setBookingPackageInfo] = useState({});
    const userInfoRef = useRef(null);
    const maritalRef = useRef(null);
    const conditionsRef = useRef(null);
    const addDocumentsRef = useRef(null);
    const advancedRef = useRef(null);
    let visibleSteps = getVisibleSteps(currentStep);
    if (!firstUser[ENABLED_DOCUMENTS_AND_TIME_BOOKING]) {
        visibleSteps = visibleSteps.filter(step => step !== ADD_DOCUMENTS);
    }
    const isBookingFieldsVisible = partner.onboardingBooking && isAdvisor;
    const isRealEstateFieldVisible = (_c = partner === null || partner === void 0 ? void 0 : partner.showRealEstateQuestions) !== null && _c !== void 0 ? _c : false;
    const isRealEstateSelected = (_d = firstUser === null || firstUser === void 0 ? void 0 : firstUser[UserFields.IS_REAL_ESTATE_PLANNED]) !== null && _d !== void 0 ? _d : false;
    // Get custom headers and sublines or fallback to the defaults
    const headerWithAdvisor = partner.headerWithAdvisor ? partner.headerWithAdvisor : 'partner.signup.title';
    const headerWithoutAdvisor = partner.headerWithoutAdvisor ? partner.headerWithoutAdvisor : 'partner.signup.title';
    const sublineWithAdvisor = partner.sublineWithAdvisor ? partner.sublineWithAdvisor : 'partner.signup.contentAdvisor';
    const sublineWithoutAdvisor = partner.sublineWithoutAdvisor ? partner.sublineWithoutAdvisor : 'partner.signup.content';
    /**
     * Update a user object.
     *
     * @param {string} field
     * @param {string|null} value
     * @param {string} userToUpdate
     * @param {array} fieldsToRemove
     */
    const updateUser = (fields, userToUpdate, fieldsToRemove = []) => {
        const updateUserFields = (user) => {
            const newUser = Object.assign(Object.assign({}, user), fields);
            fieldsToRemove.forEach(fieldToRemove => {
                delete newUser[fieldToRemove];
            });
            return newUser;
        };
        if (userToUpdate === UserToUpdate.BOTH) {
            setFirstUser(updateUserFields(firstUser));
            setSecondUser(updateUserFields(secondUser));
        }
        else if (userToUpdate === UserToUpdate.FIRST) {
            setFirstUser(updateUserFields(firstUser));
        }
        else {
            setSecondUser(updateUserFields(secondUser));
        }
    };
    /**
     * Validate the values set on a user.
     *
     * @param {string} userToValidate
     *
     * @return {object}
     *   An object of errors, if any.
     */
    const validateUser = (userToValidate) => {
        const newErrors = {};
        const user = userToValidate === UserToUpdate.FIRST ? firstUser : secondUser;
        if (isAdvisor && !user[ADVISOR_ID]) {
            newErrors[`${userToValidate}AdvisorId`] = T(`partner.signup.errors.${userToValidate}.advisorId`);
        }
        if (isAdvisor && partner.isBranchRequired && !user[BRANCH_ID]) {
            newErrors[`${userToValidate}BranchId`] = T(`partner.signup.errors.${userToValidate}.branchId`);
        }
        if (visibleSteps.includes(USER_INFO)) {
            if (user[FIRST_NAME] == null || user[FIRST_NAME].length === 0) {
                newErrors[`${userToValidate}FirstName`] = T(`partner.signup.errors.${userToValidate}.firstname`);
            }
            if (user[LAST_NAME] == null || user[LAST_NAME].length === 0) {
                newErrors[`${userToValidate}LastName`] = T(`partner.signup.errors.${userToValidate}.lastname`);
            }
            if (user[EMAIL] == null || user[EMAIL].length === 0) {
                newErrors[`${userToValidate}Email`] = T(`partner.signup.errors.${userToValidate}.email`);
            }
            else if (!isEmailAddressValid(user[EMAIL])) {
                newErrors[`${userToValidate}Email`] = T(`partner.signup.errors.${userToValidate}.emailInvalid`);
            }
            if (user[PHONE] == null || user[PHONE].length === 0) {
                newErrors[`${userToValidate}Phone`] = T(`partner.signup.errors.${userToValidate}.phone`);
            }
        }
        return newErrors;
    };
    const validatePage = (validateConsents = false) => {
        let newErrors = validateUser(UserToUpdate.FIRST);
        // If the partner is present, validate the input fields for the partner.
        if (firstUser[PARTNER_PRESENT]) {
            newErrors = Object.assign(Object.assign({}, newErrors), validateUser(UserToUpdate.SECOND));
        }
        if (validateConsents) {
            let consents = [];
            if (partner.consent) {
                consents = Array.isArray(partner.consent.consents) ? partner.consent.consents : Object.values(partner.consent.consents);
            }
            const missingConsents = consents
                .filter(consent => consent.visible.includes('SignupForm'))
                .filter(consent => consent.required === true)
                .filter(consent => {
                const consentId = consent.text.replace(/[^a-z]/g, '');
                const consentValue = firstUser[consentId];
                // All consents must be considered.
                if (typeof consentValue === 'undefined') {
                    return true;
                }
                return consentValue === false || consentValue === 'Nej';
            })
                .filter(consent => {
                if (firstUser[ENABLED_DOCUMENTS_AND_TIME_BOOKING]) {
                    return !consent.options.some(option => ['8-12', '12-17', '17-21'].includes(option.text));
                }
                return consent;
            })
                .map(consent => consent.text.replace(/[^a-z]/g, ''));
            missingConsents.forEach((missingConsent) => {
                newErrors[missingConsent] = true;
            });
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    return (_jsxs(_Fragment, { children: [(errors === null || errors === void 0 ? void 0 : errors.save) != null && (_jsx(Message, { message: errors.save, isSuccessful: false })), currentStep !== END && (_jsxs(_Fragment, { children: [_jsxs(StepWrapper, Object.assign({ color: 'white' }, { children: [_jsx("h1", Object.assign({ className: 'text-center tailwind-reset text-5xl signup-form-header' }, { children: T(isAdvisor ? headerWithAdvisor : headerWithoutAdvisor) })), _jsx("p", Object.assign({ className: 'text-center signup-form-subline' }, { children: T(isAdvisor ? sublineWithAdvisor : sublineWithoutAdvisor, { partner: partner.name }) }))] })), isAdvisor && (_jsx(StepWrapper, Object.assign({ color: 'white' }, { children: _jsx(ErrorBoundary, { children: _jsx(AdvisorStep, { partner: partner, user: firstUser, updateUser: updateUser, errors: errors, branches: branches, onSave: currentStep === ADVISOR ? handleClickSaveAdvisor : null }) }) }))), !isAdvisor && forceAdvisorField && (_jsx(StepWrapper, Object.assign({ color: 'white' }, { children: _jsx(ErrorBoundary, { children: _jsx(UserAdvisorStep, { partner: partner, user: firstUser, updateUser: updateUser, errors: errors, onSave: currentStep === ADVISOR ? handleClickSaveAdvisor : null }) }) }))), visibleSteps.includes(USER_INFO) && (_jsx(StepWrapper, Object.assign({ ref: userInfoRef, color: 'white' }, { children: _jsx(ErrorBoundary, { children: _jsx(UserInfoStep, { partner: partner, user: firstUser, updateUser: updateUser, errors: errors, onSave: currentStep === USER_INFO ? handleClickSaveUserInfo : null }) }) }))), visibleSteps.includes(MARITAL_STATUS_AND_BOOKING) && (_jsx(StepWrapper, Object.assign({ ref: maritalRef }, { children: _jsx(ErrorBoundary, { children: _jsx(MaritalStatusAndBookingStep, { user: firstUser, updateUser: updateUser, updateAdditionalData: updateAdditionalData, onSave: currentStep === MARITAL_STATUS_AND_BOOKING ? handleClickSaveMaritalStatusAndBooking : null, showBookingField: isBookingFieldsVisible, showRealEstateQuestion: isRealEstateFieldVisible, setCurrentStep: setCurrentStep, currentStep: currentStep }) }) }))), visibleSteps.includes(CONSENT) && (_jsx(StepWrapper, Object.assign({ ref: conditionsRef }, { children: _jsx(ErrorBoundary, { children: _jsx(ConsentsStep, { user: firstUser, partner: partner, updateUser: updateUser, onSave: currentStep === CONSENT ? handleClickSaveConsent : null, errors: errors }) }) }))), visibleSteps.includes(ADD_DOCUMENTS) && (_jsx(StepWrapper, Object.assign({ ref: addDocumentsRef }, { children: _jsx(ErrorBoundary, { children: _jsx(AddDocumentsStep, { onSave: currentStep === ADD_DOCUMENTS ? handleClickSaveAddedDocuments : null, updateAdditionalData: updateAdditionalData, partnerId: partner.id, setBookingPackageInfo: setBookingPackageInfo, isRealEstateSelected: isRealEstateSelected, currentStep: currentStep, setCurrentStep: setCurrentStep }) }) }))), visibleSteps.includes(ADVANCED) && (_jsx(StepWrapper, Object.assign({ ref: advancedRef }, { children: _jsx(ErrorBoundary, { children: _jsx(AdvancedStep, { user: firstUser, additionalData: additionalData, updateUser: updateUser, updateAdditionalData: updateAdditionalData, onClickCreate: handleClickCreateUser, isCreatUserLoading: isCreatUserLoading, partnerId: partner.id, timeBookingTitle: bookingPackageInfo === null || bookingPackageInfo === void 0 ? void 0 : bookingPackageInfo.description }) }) })))] })), currentStep === END && _jsx(EndStep, {})] }));
    function handleClickSaveAdvisor() {
        if (!validatePage()) {
            return;
        }
        setCurrentStep(USER_INFO);
    }
    function handleClickSaveUserInfo() {
        if (!validatePage()) {
            return;
        }
        setCurrentStep(MARITAL_STATUS_AND_BOOKING);
    }
    function handleClickSaveMaritalStatusAndBooking() {
        if (!validatePage()) {
            return;
        }
        setCurrentStep(CONSENT);
    }
    function handleClickSaveConsent() {
        if (!validatePage(true)) {
            return;
        }
        if (firstUser[ENABLED_DOCUMENTS_AND_TIME_BOOKING]) {
            setCurrentStep(ADD_DOCUMENTS);
        }
        else {
            setCurrentStep(ADVANCED);
        }
    }
    function handleClickSaveAddedDocuments() {
        setCurrentStep(ADVANCED);
    }
    function updateAdditionalData(data, resetToAddDocumentsStep = false) {
        setAdditionalData(Object.assign(Object.assign({}, additionalData), data));
        if (resetToAddDocumentsStep && currentStep !== ADD_DOCUMENTS) {
            setCurrentStep(ADD_DOCUMENTS);
        }
    }
    function handleClickCreateUser() {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            if (!validatePage(true) || isCreatUserLoading) {
                return;
            }
            setCreateUserLoading(true);
            try {
                const response = yield axios.post(`/api/henvisning/${partner.id}`, Object.assign({ users: [firstUser, secondUser] }, additionalData));
                if (((_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.url) != null) {
                    window.location.href = response.data.url;
                    return;
                }
                setCurrentStep(END);
            }
            catch (error) {
                const errorCode = (error === null || error === void 0 ? void 0 : error.response) && ((_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.status) ? (_c = error === null || error === void 0 ? void 0 : error.response) === null || _c === void 0 ? void 0 : _c.status : 500;
                if (errorCode === 400) {
                    if (((_d = error === null || error === void 0 ? void 0 : error.response) === null || _d === void 0 ? void 0 : _d.data) === 'Invalid advisorId') {
                        setErrors({ save: T('partner.signup.errors.advisorId') });
                    }
                    else {
                        setErrors({ save: T('partner.signup.errors.mail', { email: error.response.data }) });
                    }
                    return;
                }
                const ticketCreated = yield createTicket(firstUser, secondUser);
                if (!ticketCreated) {
                    setErrors({ save: T('partner.signup.errors.error') });
                    return;
                }
                setCurrentStep(END);
            }
            finally {
                setCreateUserLoading(false);
            }
        });
    }
};
SignupForm.propTypes = {
    partner: PropTypes.object.isRequired,
    branches: PropTypes.array,
    isAdvisor: PropTypes.bool,
    forceAdvisorField: PropTypes.bool
};
export default SignupForm;
